import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../../supabaseClient';
import { usePermissions } from '../../utils/permissions';
import {
  Box, Container, Typography, Button, TextField, Dialog, DialogTitle, DialogContent,
  DialogActions, IconButton, Alert, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TablePagination, Fab, Card, CardContent, Snackbar, useTheme,
  alpha, Fade, LinearProgress, InputAdornment, Tooltip, FormControl, InputLabel,
  Select, MenuItem, Chip, Grid, Divider
} from '@mui/material';
import {
  Add as AddIcon, Delete as DeleteIcon, Edit as EditIcon,
  Close as CloseIcon, Search as SearchIcon,
  Receipt as ReceiptIcon, CloudUpload as CloudUploadIcon,
  Description as DescriptionIcon, Visibility as VisibilityIcon,
  CheckCircle as CheckCircleIcon, Cancel as CancelIcon,
  Verified as VerifiedIcon, FilterList as FilterListIcon
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';

const PAID_BY_OPTIONS = [
  { value: 'By Company', label: 'By Company' },
  { value: 'Employee (Reimburse)', label: 'Employee (Reimburse)' }
];

const ExpensesPage = () => {
  const { companyId } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const { canViewExpenses, canEditExpenses, canDeleteExpenses, canApproveExpenses } = usePermissions();
  
  // State management
  const [expenses, setExpenses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [companyName, setCompanyName] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [expenseToDelete, setExpenseToDelete] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  
  // Approval dialog states
  const [approvalDialogOpen, setApprovalDialogOpen] = useState(false);
  const [approvalExpense, setApprovalExpense] = useState(null);
  const [signatureName, setSignatureName] = useState('');
  const [approvalNotes, setApprovalNotes] = useState('');
  
  // View dialog states
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [viewExpense, setViewExpense] = useState(null);
  
  // Category selection dialog states
  const [categorySelectionDialogOpen, setCategorySelectionDialogOpen] = useState(false);
  const [categorySearchTerm, setCategorySearchTerm] = useState('');
  const [dialogSelectedCategory, setDialogSelectedCategory] = useState(null);
  
  // Account selection dialog states
  const [accountSelectionDialogOpen, setAccountSelectionDialogOpen] = useState(false);
  const [accountSearchTerm, setAccountSearchTerm] = useState('');
  const [dialogSelectedAccount, setDialogSelectedAccount] = useState(null);
  
  // Filter dialog selection states
  const [filterCategorySelectionDialogOpen, setFilterCategorySelectionDialogOpen] = useState(false);
  const [filterCategorySearchTerm, setFilterCategorySearchTerm] = useState('');
  const [filterDialogSelectedCategory, setFilterDialogSelectedCategory] = useState(null);
  
  const [filterEmployeeSelectionDialogOpen, setFilterEmployeeSelectionDialogOpen] = useState(false);
  const [filterEmployeeSearchTerm, setFilterEmployeeSearchTerm] = useState('');
  const [filterDialogSelectedEmployee, setFilterDialogSelectedEmployee] = useState(null);
  
  // Search and filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredExpenses, setFilteredExpenses] = useState([]);
  const [filterDialogOpen, setFilterDialogOpen] = useState(false);
  const [filters, setFilters] = useState({
    dateFrom: null,
    dateTo: null,
    category: '',
    employee: '',
    paidBy: ''
  });
  
  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  // Form data
  const [formData, setFormData] = useState({
    id: '',
    description: '',
    expense_date: dayjs(),
    category_id: '',
    paid_by: '',
    account_id: '',
    total: '',
    document_file_path: '',
    document_file_name: '',
    document_file_type: '',
    document_file_size: null
  });

  // Document file state
  const [documentFile, setDocumentFile] = useState(null);
  const [uploadingDocument, setUploadingDocument] = useState(false);

  const fetchCompanyData = async () => {
    try {
      const { data, error } = await supabase
        .from('companies')
        .select('name')
        .eq('id', companyId)
        .single();
      if (error) throw error;
      setCompanyName(data?.name || 'Company');
    } catch (err) {
      console.error('Error fetching company:', err);
      setCompanyName('Company Name');
    }
  };

  const fetchCategories = async () => {
    try {
      const { data, error } = await supabase
        .rpc('get_expense_categories_decrypted', { company_id_param: companyId });
      if (error) throw error;
      setCategories(data || []);
    } catch (err) {
      console.error('Error fetching categories:', err);
    }
  };

  const fetchAccounts = async () => {
    try {
      const { data, error } = await supabase
        .rpc('get_expense_accounts_decrypted', { company_id_param: companyId });
      if (error) throw error;
      // Sort by account name
      const sortedData = (data || []).sort((a, b) => 
        (a.account_name || '').localeCompare(b.account_name || '')
      );
      setAccounts(sortedData);
    } catch (err) {
      console.error('Error fetching accounts:', err);
    }
  };

  const fetchEmployees = async () => {
    try {
      // Fetch all users in the company
      const { data: companyUsers, error: companyUsersError } = await supabase
        .from('company_users')
        .select('user_id')
        .eq('company_id', companyId)
        .eq('status', 'active');
      
      if (companyUsersError) throw companyUsersError;
      
      const userIds = companyUsers.map(cu => cu.user_id);
      
      if (userIds.length > 0) {
        const { data: profiles, error: profilesError } = await supabase
          .from('profiles')
          .select('id, first_name, last_name, full_name')
          .in('id', userIds);
        
        if (profilesError) throw profilesError;
        setEmployees(profiles || []);
      }
    } catch (err) {
      console.error('Error fetching employees:', err);
    }
  };

  const fetchExpenses = async () => {
    setLoading(true);
    try {
      // Fetch decrypted expenses
      const { data: expensesData, error: expensesError } = await supabase
        .rpc('get_expenses_decrypted', { company_id_param: companyId });
      if (expensesError) throw expensesError;

      // Fetch employee profiles
      const { data: companyUsers, error: companyUsersError } = await supabase
        .from('company_users')
        .select('user_id')
        .eq('company_id', companyId)
        .eq('status', 'active');
      
      if (companyUsersError) throw companyUsersError;
      
      const userIds = companyUsers.map(cu => cu.user_id);
      let profiles = [];
      
      if (userIds.length > 0) {
        const { data: profilesData, error: profilesError } = await supabase
          .from('profiles')
          .select('id, first_name, last_name, full_name')
          .in('id', userIds);
        
        if (profilesError) throw profilesError;
        profiles = profilesData || [];
      }

      // Fetch decrypted categories and accounts
      const { data: categoriesData, error: categoriesError } = await supabase
        .rpc('get_expense_categories_decrypted', { company_id_param: companyId });
      if (categoriesError) throw categoriesError;

      const { data: accountsData, error: accountsError } = await supabase
        .rpc('get_expense_accounts_decrypted', { company_id_param: companyId });
      if (accountsError) throw accountsError;

      // Map related data to expenses
      const expensesWithRelations = (expensesData || []).map(expense => ({
        ...expense,
        employee: profiles.find(p => p.id === expense.employee_id) || null,
        category: expense.category_id 
          ? categoriesData.find(cat => cat.id === expense.category_id)
          : null,
        account: expense.account_id
          ? accountsData.find(acc => acc.id === expense.account_id)
          : null
      }));

      // Sort by expense date descending
      expensesWithRelations.sort((a, b) => 
        new Date(b.expense_date) - new Date(a.expense_date)
      );

      setExpenses(expensesWithRelations);
    } catch (err) {
      console.error('Error fetching expenses:', err);
      setError('Failed to load expenses.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCompanyData();
    fetchCategories();
    fetchAccounts();
    fetchEmployees();
    fetchExpenses();
  }, [companyId]);

  const handleOpenDialog = (expense = null) => {
    if (expense) {
      setEditMode(true);
      setFormData({
        id: expense.id,
        description: expense.description || '',
        expense_date: expense.expense_date ? dayjs(expense.expense_date) : dayjs(),
        category_id: expense.category_id || '',
        paid_by: expense.paid_by || '',
        account_id: expense.account_id || '',
        total: expense.total || '',
        document_file_path: expense.document_file_path || '',
        document_file_name: expense.document_file_name || '',
        document_file_type: expense.document_file_type || '',
        document_file_size: expense.document_file_size || null
      });
    } else {
      setEditMode(false);
      setFormData({
        id: '',
        description: '',
        expense_date: dayjs(),
        category_id: '',
        paid_by: '',
        account_id: '',
        total: '',
        document_file_path: '',
        document_file_name: '',
        document_file_type: '',
        document_file_size: null
      });
    }
    setDocumentFile(null);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditMode(false);
  };

  const handleFileUpload = (event) => {
    const file = event.target.files?.[0];
    if (file) {
      setDocumentFile(file);
    }
  };

  const uploadDocumentFile = async (file) => {
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;
      const filePath = `${companyId}/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('expenses-documents')
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      return {
        path: filePath,
        name: file.name,
        type: file.type,
        size: file.size
      };
    } catch (error) {
      console.error('Error uploading file:', error);
      throw error;
    }
  };

  const removeDocumentFile = async () => {
    try {
      if (formData.document_file_path) {
        const { error } = await supabase.storage
          .from('expenses-documents')
          .remove([formData.document_file_path]);

        if (error) throw error;
      }
      setFormData(prev => ({
        ...prev,
        document_file_path: '',
        document_file_name: '',
        document_file_type: '',
        document_file_size: null
      }));
      setDocumentFile(null);
      setSnackbar({ open: true, message: 'Document removed successfully', severity: 'success' });
    } catch (error) {
      console.error('Error removing file:', error);
      setSnackbar({ open: true, message: 'Error removing file', severity: 'error' });
    }
  };

  const downloadDocumentFile = async (filePath, fileName) => {
    try {
      const { data, error } = await supabase.storage
        .from('expenses-documents')
        .download(filePath);

      if (error) throw error;

      const url = URL.createObjectURL(data);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading file:', error);
      setSnackbar({ open: true, message: 'Error downloading file', severity: 'error' });
    }
  };

  const viewDocumentFile = async (filePath, fileName) => {
    try {
      const { data, error } = await supabase.storage
        .from('expenses-documents')
        .createSignedUrl(filePath, 3600); // 1 hour expiry

      if (error) throw error;

      window.open(data.signedUrl, '_blank');
    } catch (error) {
      console.error('Error viewing file:', error);
      setSnackbar({ open: true, message: 'Error viewing file', severity: 'error' });
    }
  };

  const handleSubmit = async () => {
    if (!formData.description.trim()) {
      setError('Description is required.');
      return;
    }
    
    if (!formData.total || parseFloat(formData.total) <= 0) {
      setError('Total amount must be greater than 0.');
      return;
    }
    
    setSubmitting(true);
    setError(null);

    try {
      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      
      // Upload document file if a new one was selected
      let documentFileData = {
        document_file_path: formData.document_file_path,
        document_file_name_encrypted: formData.document_file_name,
        document_file_type: formData.document_file_type,
        document_file_size: formData.document_file_size
      };

      if (documentFile) {
        setUploadingDocument(true);
        const uploadedFile = await uploadDocumentFile(documentFile);
        
        // Encrypt the document file name
        const { data: encryptedFileName, error: fileNameError } = await supabase
          .rpc('encrypt_expense_field', { field_value: uploadedFile.name });
        if (fileNameError) throw fileNameError;
        
        documentFileData = {
          document_file_path: uploadedFile.path,
          document_file_name_encrypted: encryptedFileName,
          document_file_type: uploadedFile.type,
          document_file_size: uploadedFile.size
        };
        setUploadingDocument(false);
      }

      // Encrypt sensitive fields
      const { data: encryptedDescription, error: descError } = await supabase
        .rpc('encrypt_expense_field', { field_value: formData.description.trim() });
      if (descError) throw descError;

      const { data: encryptedTotal, error: totalError } = await supabase
        .rpc('encrypt_expense_field', { field_value: formData.total.toString() });
      if (totalError) throw totalError;

      const expenseData = {
        company_id: companyId,
        employee_id: user.id,
        description_encrypted: encryptedDescription,
        expense_date: formData.expense_date ? formData.expense_date.format('YYYY-MM-DD') : null,
        category_id: formData.category_id || null,
        paid_by: formData.paid_by || null,
        account_id: formData.account_id || null,
        total_encrypted: encryptedTotal,
        ...documentFileData
      };

      if (editMode) {
        const { error } = await supabase
          .from('expenses')
          .update(expenseData)
          .eq('id', formData.id);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('expenses')
          .insert(expenseData);
        if (error) throw error;
      }

      await fetchExpenses();
      handleCloseDialog();
      setSnackbar({
        open: true,
        message: `Expense ${editMode ? 'updated' : 'added'} successfully`,
        severity: 'success'
      });
    } catch (err) {
      console.error('Error saving expense:', err);
      setError(`Failed to ${editMode ? 'update' : 'create'} expense.`);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteClick = (expense) => {
    setExpenseToDelete(expense);
    setDeleteConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!expenseToDelete) return;
    
    try {
      // Delete document file from storage bucket if it exists
      if (expenseToDelete.document_file_path) {
        try {
          const { error: storageError } = await supabase.storage
            .from('expenses-documents')
            .remove([expenseToDelete.document_file_path]);
          
          if (storageError) {
            console.error('Error deleting document file from storage:', storageError);
          }
        } catch (storageErr) {
          console.error('Error deleting document file:', storageErr);
        }
      }

      const { error } = await supabase
        .from('expenses')
        .delete()
        .eq('id', expenseToDelete.id);
      if (error) throw error;
      
      await fetchExpenses();
      setSnackbar({ open: true, message: 'Expense deleted successfully', severity: 'success' });
    } catch (err) {
      setSnackbar({ open: true, message: `Error deleting expense: ${err.message}`, severity: 'error' });
    }
    setDeleteConfirmOpen(false);
    setExpenseToDelete(null);
  };

  const handleReviewExpense = (expense) => {
    setApprovalExpense(expense);
    setSignatureName('');
    setApprovalNotes('');
    setApprovalDialogOpen(true);
  };

  const handleApprovalSubmit = async () => {
    try {
      if (!signatureName.trim()) {
        setSnackbar({ open: true, message: 'Signature name is required for approval', severity: 'error' });
        return;
      }

      const { data: { user } } = await supabase.auth.getUser();

      // Encrypt approval notes if provided
      let encryptedApprovalNotes = null;
      if (approvalNotes && approvalNotes.trim()) {
        const { data: encrypted, error: encryptError } = await supabase
          .rpc('encrypt_expense_field', { field_value: approvalNotes.trim() });
        if (encryptError) throw encryptError;
        encryptedApprovalNotes = encrypted;
      }

      const approvalData = {
        approved_by_signature: signatureName,
        approved_at: new Date().toISOString(),
        approved_by: user.id,
        approval_notes_encrypted: encryptedApprovalNotes,
        status: 'approved'
      };

      const { error } = await supabase
        .from('expenses')
        .update(approvalData)
        .eq('id', approvalExpense.id);

      if (error) throw error;

      setApprovalDialogOpen(false);
      setSignatureName('');
      setApprovalNotes('');
      setApprovalExpense(null);
      await fetchExpenses();
      setSnackbar({ open: true, message: 'Expense approved successfully', severity: 'success' });
    } catch (err) {
      console.error('Error approving expense:', err);
      setSnackbar({ open: true, message: `Error approving expense: ${err.message}`, severity: 'error' });
    }
  };

  const handleUnapproveExpense = async (expense) => {
    try {
      const unapprovalData = {
        approved_by_signature: null,
        approved_at: null,
        approved_by: null,
        approval_notes_encrypted: null,
        status: 'pending'
      };

      const { error } = await supabase
        .from('expenses')
        .update(unapprovalData)
        .eq('id', expense.id);

      if (error) throw error;

      await fetchExpenses();
      setSnackbar({ open: true, message: 'Expense unapproved successfully', severity: 'success' });
    } catch (err) {
      console.error('Error unapproving expense:', err);
      setSnackbar({ open: true, message: `Error unapproving expense: ${err.message}`, severity: 'error' });
    }
  };

  const handleViewExpense = (expense) => {
    setViewExpense(expense);
    setViewDialogOpen(true);
  };

  const formatDate = (date) => {
    if (!date) return '—';
    return dayjs(date).format('MMM DD, YYYY');
  };

  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return '—';
    return new Intl.NumberFormat('en-US', { 
      style: 'currency', 
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2 
    }).format(amount);
  };

  // Apply filters and search
  useEffect(() => {
    let filtered = [...expenses];

    // Apply date range filter
    if (filters.dateFrom) {
      filtered = filtered.filter(exp => 
        dayjs(exp.expense_date).isAfter(dayjs(filters.dateFrom).subtract(1, 'day'))
      );
    }
    if (filters.dateTo) {
      filtered = filtered.filter(exp => 
        dayjs(exp.expense_date).isBefore(dayjs(filters.dateTo).add(1, 'day'))
      );
    }

    // Apply category filter
    if (filters.category) {
      filtered = filtered.filter(exp => exp.category_id === filters.category);
    }

    // Apply employee filter
    if (filters.employee) {
      filtered = filtered.filter(exp => exp.employee_id === filters.employee);
    }

    // Apply paid by filter
    if (filters.paidBy) {
      filtered = filtered.filter(exp => exp.paid_by === filters.paidBy);
    }

    // Apply search term
    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(exp => 
        exp.description?.toLowerCase().includes(searchLower) ||
        exp.employee?.full_name?.toLowerCase().includes(searchLower) ||
        exp.category?.name?.toLowerCase().includes(searchLower)
      );
    }

    setFilteredExpenses(filtered);
    setPage(0);
  }, [searchTerm, filters, expenses]);

  const clearFilters = () => {
    setFilters({
      dateFrom: null,
      dateTo: null,
      category: '',
      employee: '',
      paidBy: ''
    });
    setFilterDialogOpen(false);
  };

  const activeFiltersCount = Object.values(filters).filter(v => v).length;

  // Category selection dialog handlers
  const handleOpenCategorySelectionDialog = () => {
    setCategorySelectionDialogOpen(true);
    setCategorySearchTerm('');
    setDialogSelectedCategory(null);
  };

  const handleCloseCategorySelectionDialog = () => {
    setCategorySelectionDialogOpen(false);
    setCategorySearchTerm('');
    setDialogSelectedCategory(null);
  };

  const handleDialogCategorySelect = (category) => {
    setDialogSelectedCategory(category);
  };

  const handleConfirmCategorySelection = () => {
    if (dialogSelectedCategory) {
      setFormData(prev => ({ ...prev, category_id: dialogSelectedCategory.id }));
      setCategorySelectionDialogOpen(false);
      setCategorySearchTerm('');
      setDialogSelectedCategory(null);
    }
  };

  const getFilteredCategoriesForSelection = () => {
    if (!categorySearchTerm) return categories || [];
    
    const searchLower = categorySearchTerm.toLowerCase();
    return (categories || []).filter(category => 
      category.name.toLowerCase().includes(searchLower)
    );
  };

  // Account selection dialog handlers
  const handleOpenAccountSelectionDialog = () => {
    setAccountSelectionDialogOpen(true);
    setAccountSearchTerm('');
    setDialogSelectedAccount(null);
  };

  const handleCloseAccountSelectionDialog = () => {
    setAccountSelectionDialogOpen(false);
    setAccountSearchTerm('');
    setDialogSelectedAccount(null);
  };

  const handleDialogAccountSelect = (account) => {
    setDialogSelectedAccount(account);
  };

  const handleConfirmAccountSelection = () => {
    if (dialogSelectedAccount) {
      setFormData(prev => {
        const updates = { 
          account_id: dialogSelectedAccount.id,
          paid_by: 'By Company' // Auto-select "By Company" when account is selected
        };
        // Auto-fill category if account has one and category is not already set
        if (dialogSelectedAccount.category_id && !prev.category_id) {
          updates.category_id = dialogSelectedAccount.category_id;
        }
        return { ...prev, ...updates };
      });
      setAccountSelectionDialogOpen(false);
      setAccountSearchTerm('');
      setDialogSelectedAccount(null);
    }
  };

  const getFilteredAccountsForSelection = () => {
    if (!accountSearchTerm) return accounts || [];
    
    const searchLower = accountSearchTerm.toLowerCase();
    return (accounts || []).filter(account => 
      account.account_name.toLowerCase().includes(searchLower) ||
      account.last_four_digits.includes(searchLower)
    );
  };

  // Filter dialog handlers for Category
  const handleOpenFilterCategorySelectionDialog = () => {
    setFilterCategorySelectionDialogOpen(true);
    setFilterCategorySearchTerm('');
    setFilterDialogSelectedCategory(null);
  };

  const handleCloseFilterCategorySelectionDialog = () => {
    setFilterCategorySelectionDialogOpen(false);
    setFilterCategorySearchTerm('');
    setFilterDialogSelectedCategory(null);
  };

  const handleFilterDialogCategorySelect = (category) => {
    setFilterDialogSelectedCategory(category);
  };

  const handleConfirmFilterCategorySelection = () => {
    if (filterDialogSelectedCategory) {
      setFilters(prev => ({ ...prev, category: filterDialogSelectedCategory.id }));
      setFilterCategorySelectionDialogOpen(false);
      setFilterCategorySearchTerm('');
      setFilterDialogSelectedCategory(null);
    }
  };

  const getFilteredCategoriesForFilterSelection = () => {
    if (!filterCategorySearchTerm) return categories || [];
    
    const searchLower = filterCategorySearchTerm.toLowerCase();
    return (categories || []).filter(category => 
      category.name.toLowerCase().includes(searchLower)
    );
  };

  // Filter dialog handlers for Employee
  const handleOpenFilterEmployeeSelectionDialog = () => {
    setFilterEmployeeSelectionDialogOpen(true);
    setFilterEmployeeSearchTerm('');
    setFilterDialogSelectedEmployee(null);
  };

  const handleCloseFilterEmployeeSelectionDialog = () => {
    setFilterEmployeeSelectionDialogOpen(false);
    setFilterEmployeeSearchTerm('');
    setFilterDialogSelectedEmployee(null);
  };

  const handleFilterDialogEmployeeSelect = (employee) => {
    setFilterDialogSelectedEmployee(employee);
  };

  const handleConfirmFilterEmployeeSelection = () => {
    if (filterDialogSelectedEmployee) {
      setFilters(prev => ({ ...prev, employee: filterDialogSelectedEmployee.id }));
      setFilterEmployeeSelectionDialogOpen(false);
      setFilterEmployeeSearchTerm('');
      setFilterDialogSelectedEmployee(null);
    }
  };

  const getFilteredEmployeesForFilterSelection = () => {
    if (!filterEmployeeSearchTerm) return employees || [];
    
    const searchLower = filterEmployeeSearchTerm.toLowerCase();
    return (employees || []).filter(employee => 
      employee.full_name.toLowerCase().includes(searchLower)
    );
  };

  // Permission check
  if (!canViewExpenses()) {
    return (
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          justifyContent: 'center',
          minHeight: '70vh',
          textAlign: 'center'
        }}>
          <Card sx={{
            p: 8,
            borderRadius: 3,
            border: `1px solid ${theme.palette.grey[400]}`,
            background: 'white',
            boxShadow: `0 12px 40px ${alpha(theme.palette.grey[900], 0.08)}`,
            maxWidth: 600,
            width: '100%',
            position: 'relative',
            overflow: 'hidden',
            cursor: 'default',
            transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
            '&:hover': {
              transform: 'translateY(-8px)',
              boxShadow: `0 20px 60px ${alpha(theme.palette.grey[900], 0.15)}`,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
            },
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: 4,
              background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.grey[400]})`,
              opacity: 0.8,
              transition: 'opacity 0.3s ease'
            },
            '&:hover::before': {
              opacity: 1
            }
          }}>
            <Box sx={{ 
              p: 3, 
              borderRadius: 3, 
              background: `linear-gradient(${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.primary.main, 0.02)})`,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
              mb: 6,
              display: 'inline-flex',
              boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.15)}`
            }}>
              <ReceiptIcon sx={{ fontSize: 48, color: 'text.secondary' }} />
            </Box>
            <Typography variant="h3" sx={{ 
              mb: 3, 
              fontWeight: 500, 
              color: 'text.primary',
              fontSize: '2.5rem',
              letterSpacing: '-0.8px'
            }}>
              Access Restricted
            </Typography>
            <Typography variant="h6" sx={{ 
              color: 'text.secondary', 
              mb: 4,
              fontWeight: 500,
              fontSize: '1.2rem'
            }}>
              Expenses Permissions Required
            </Typography>
            <Typography variant="body1" sx={{ 
              color: 'text.secondary',
              lineHeight: 1.7,
              mb: 6,
              fontSize: '1.05rem',
              maxWidth: 450,
              mx: 'auto'
            }}>
              You don't currently have the necessary permissions to access the Expenses section. 
              Contact your system administrator to request access or verify your role permissions.
            </Typography>
            <Button
              variant="outlined"
              onClick={() => navigate(-1)}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 500,
                px: 4,
                py: 1.5,
                fontSize: '1rem'
              }}
            >
              Return to Previous Page
            </Button>
          </Card>
        </Box>
      </Container>
    );
  }

  if (loading && expenses.length === 0) {
    return (
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <Container disableGutters maxWidth="xl" sx={{ px: 3, py: 4, pb: 16 }}>
          <Card sx={{ 
            borderRadius: 3,
            mt: 10,
            mx: 'auto',
            maxWidth: 600,
            border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
            boxShadow: `0 12px 32px ${alpha('#C62828', 0.15)}`,
          }}>
            <CardContent sx={{ p: 6, textAlign: 'center' }}>
              <Box sx={{ mb: 3 }}>
                <Box sx={{
                  display: 'inline-flex',
                  p: 3,
                  borderRadius: '50%',
                  bgcolor: alpha('#C62828', 0.1),
                  mb: 2
                }}>
                  <ReceiptIcon sx={{ fontSize: 48, color: '#C62828' }} />
                </Box>
              </Box>
              <Typography variant="h6" sx={{
                fontWeight: 500,
                color: '#C62828',
                mb: 3
              }}>
                Loading Expenses
              </Typography>
              <LinearProgress />
            </CardContent>
          </Card>
        </Container>
      </LocalizationProvider>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Fade in timeout={800}>
        <Box>
          <Container maxWidth="xl" sx={{ py: 3, pb: 16 }}>
            {/* Header Section */}
            <Box sx={{ mb: 3 }}>
              <Card sx={{
                borderRadius: 3,
                mt: 5,
                mb: 3,
                border: `1px solid ${theme.palette.grey[400]}`,
                background: theme.palette.grey[100],
                boxShadow: 'none',
                borderTop: '3px solid #C62828',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: `0 6px 20px ${alpha('#C62828', 0.15)}`,
                },
              }}>
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5, mb: 2 }}>
                    <Box sx={{
                      p: 2,
                      borderRadius: 3,
                      background: alpha('#C62828', 0.08),
                      border: `1px solid ${alpha('#C62828', 0.15)}`,
                    }}>
                      <ReceiptIcon sx={{ fontSize: 32, color: '#C62828' }} />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="h4" sx={{
                        fontWeight: 500,
                        color: '#C62828',
                        mb: 1,
                        fontSize: '2rem',
                        letterSpacing: '-0.5px'
                      }}>
                        Expenses
                      </Typography>
                      <Typography variant="subtitle1" sx={{ 
                        color: 'text.secondary', 
                        fontWeight: 500,
                        fontSize: '1rem'
                      }}>
                        {companyName}
                      </Typography>
                    </Box>
                  </Box>
                  <Typography variant="body2" sx={{ 
                    color: 'text.secondary', 
                    maxWidth: 700, 
                    lineHeight: 1.6,
                    fontSize: '0.95rem',
                    fontWeight: 500
                  }}>
                    Track and manage business expenses with approval workflow.
                  </Typography>
                </CardContent>
              </Card>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>{error}</Alert>}

            {/* Search Bar and Filters */}
            <Card sx={{
              height: 100,
              mb: 2,
              border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
              borderRadius: 3,
              background: `linear-gradient(135deg, ${alpha(theme.palette.grey[100], 0.8)}, ${alpha(theme.palette.grey[50], 0.9)})`,
              boxShadow: `0 8px 24px ${alpha(theme.palette.grey[500], 0.12)}`,
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: `0 20px 40px ${alpha(theme.palette.primary.main, 0.25)}`,
                border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
              }
            }}>
              <CardContent>
                <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
                  <TextField
                    placeholder="Search expenses..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    size="small"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      minWidth: 280,
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 3,
                        border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                        background: 'white',
                        height: 40,
                        '& fieldset': { border: 'none' },
                        '&:hover': {
                          border: `1px solid ${theme.palette.primary.main}`,
                          boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`
                        },
                        '&.Mui-focused': {
                          border: `1px solid ${theme.palette.primary.main}`,
                          boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.25)}`
                        }
                      },
                      '& .MuiInputBase-input': {
                        fontSize: '0.9rem',
                        fontWeight: 500,
                        py: 1.25
                      }
                    }}
                  />
                  
                  <Button
                    variant="outlined"
                    startIcon={<FilterListIcon />}
                    onClick={() => setFilterDialogOpen(true)}
                    sx={{
                      height: 40,
                      borderRadius: 3,
                      textTransform: 'none'
                    }}
                  >
                    Filters
                    {activeFiltersCount > 0 && (
                      <Chip 
                        label={activeFiltersCount} 
                        size="small" 
                        color="primary" 
                        sx={{ ml: 1, height: 20 }}
                      />
                    )}
                  </Button>
                  
                  <Box sx={{ flex: 1 }} />
                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                    {`${filteredExpenses.length} expense${filteredExpenses.length !== 1 ? 's' : ''}`}
                  </Typography>
                </Box>
              </CardContent>
            </Card>

            {/* Expenses Table */}
            <Paper sx={{ 
              borderRadius: 3, 
              border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
              background: `linear-gradient(135deg, ${alpha(theme.palette.grey[100], 0.8)}, ${alpha(theme.palette.grey[50], 0.9)})`,
              boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.15)}`,
              overflow: 'hidden',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: `0 20px 40px ${alpha(theme.palette.primary.main, 0.25)}`,
                border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
              }
            }}>
              <TableContainer sx={{ overflowX: 'hidden' }}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 500, fontSize: '0.85rem', bgcolor: 'white', borderBottom: `2px solid ${theme.palette.primary.main}`, color: 'primary.main', py: 2, px: 2 }}>
                        Employee
                      </TableCell>
                      <TableCell sx={{ fontWeight: 500, fontSize: '0.85rem', bgcolor: 'white', borderBottom: `2px solid ${theme.palette.primary.main}`, color: 'primary.main', py: 2, px: 2 }}>
                        Description
                      </TableCell>
                      <TableCell sx={{ fontWeight: 500, fontSize: '0.85rem', bgcolor: 'white', borderBottom: `2px solid ${theme.palette.primary.main}`, color: 'primary.main', py: 2, px: 2 }}>
                        Date
                      </TableCell>
                      <TableCell sx={{ fontWeight: 500, fontSize: '0.85rem', bgcolor: 'white', borderBottom: `2px solid ${theme.palette.primary.main}`, color: 'primary.main', py: 2, px: 2 }}>
                        Category
                      </TableCell>
                      <TableCell sx={{ fontWeight: 500, fontSize: '0.85rem', bgcolor: 'white', borderBottom: `2px solid ${theme.palette.primary.main}`, color: 'primary.main', py: 2, px: 2 }}>
                        Paid By
                      </TableCell>
                      <TableCell sx={{ fontWeight: 500, fontSize: '0.85rem', bgcolor: 'white', borderBottom: `2px solid ${theme.palette.primary.main}`, color: 'primary.main', py: 2, px: 2 }}>
                        Card Number
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 500, fontSize: '0.85rem', bgcolor: 'white', borderBottom: `2px solid ${theme.palette.primary.main}`, color: 'primary.main', py: 2, px: 2 }}>
                        Total
                      </TableCell>
                      <TableCell sx={{ fontWeight: 500, fontSize: '0.85rem', bgcolor: 'white', borderBottom: `2px solid ${theme.palette.primary.main}`, color: 'primary.main', py: 2, px: 2 }}>
                        Status
                      </TableCell>
                      <TableCell align="center" sx={{ fontWeight: 500, fontSize: '0.85rem', bgcolor: 'white', borderBottom: `2px solid ${theme.palette.primary.main}`, color: 'primary.main', py: 2, px: 2, width: 180 }}>
                        Actions
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredExpenses.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((expense) => (
                      <TableRow 
                        key={expense.id}
                        hover
                        onClick={() => handleViewExpense(expense)}
                        sx={{ 
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          bgcolor: 'white',
                          '&:hover': { 
                            bgcolor: 'grey.50',
                            transform: 'translateX(4px)',
                            boxShadow: 'inset 4px 0 0 black'
                          }
                        }}
                      >
                        <TableCell sx={{ py: 2, px: 2 }}>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {expense.employee?.full_name || '—'}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ py: 2, px: 2 }}>
                          <Typography variant="body2">
                            {expense.description}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ py: 2, px: 2 }}>
                          {formatDate(expense.expense_date)}
                        </TableCell>
                        <TableCell sx={{ py: 2, px: 2 }}>
                          {expense.category ? (
                            <Chip 
                              label={expense.category.name} 
                              size="small"
                              color="primary"
                              variant="outlined"
                            />
                          ) : '—'}
                        </TableCell>
                        <TableCell sx={{ py: 2, px: 2 }}>
                          {expense.paid_by || '—'}
                        </TableCell>
                        <TableCell sx={{ py: 2, px: 2 }}>
                          {expense.account ? (
                            <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                              •••• {expense.account.last_four_digits}
                            </Typography>
                          ) : '—'}
                        </TableCell>
                        <TableCell align="right" sx={{ py: 2, px: 2 }}>
                          <Typography variant="body2" sx={{ fontWeight: 500, color: 'primary.main' }}>
                            {formatCurrency(expense.total)}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ py: 2, px: 2 }}>
                          {expense.status === 'approved' ? (
                            <Chip label="Approved" color="success" size="small" icon={<VerifiedIcon />} />
                          ) : (
                            <Chip label="Pending" color="default" size="small" />
                          )}
                        </TableCell>
                        <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                          <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                            <Tooltip title={canEditExpenses() ? "Edit" : "No edit permission"}>
                              <span>
                                <IconButton 
                                  size="small"
                                  onClick={() => handleOpenDialog(expense)}
                                  disabled={!canEditExpenses()}
                                  sx={{
                                    color: canEditExpenses() ? 'primary.main' : 'action.disabled',
                                    border: `1px solid ${alpha(theme.palette.grey[400], 0.3)}`,
                                    borderRadius: 1,
                                    transition: 'all 0.2s cubic-bezier(0.4, 0.0, 0.2, 1)',
                                    '&:hover': canEditExpenses() ? {
                                      bgcolor: 'primary.main',
                                      color: 'white',
                                      transform: 'scale(1.05)',
                                      boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.3)}`
                                    } : {},
                                    '&.Mui-disabled': {
                                      color: 'action.disabled',
                                      border: `1px solid ${alpha(theme.palette.grey[400], 0.2)}`
                                    }
                                  }}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                            <Tooltip title={canDeleteExpenses() ? "Delete" : "No delete permission"}>
                              <span>
                                <IconButton 
                                  size="small"
                                  onClick={() => handleDeleteClick(expense)}
                                  disabled={!canDeleteExpenses()}
                                  sx={{
                                    color: canDeleteExpenses() ? 'error.main' : 'action.disabled',
                                    border: `1px solid ${alpha(theme.palette.grey[400], 0.3)}`,
                                    borderRadius: 1,
                                    transition: 'all 0.2s cubic-bezier(0.4, 0.0, 0.2, 1)',
                                    '&:hover': canDeleteExpenses() ? {
                                      bgcolor: 'error.main',
                                      color: 'white',
                                      transform: 'scale(1.05)',
                                      boxShadow: `0 2px 8px ${alpha(theme.palette.error.main, 0.3)}`
                                    } : {},
                                    '&.Mui-disabled': {
                                      color: 'action.disabled',
                                      border: `1px solid ${alpha(theme.palette.grey[400], 0.2)}`
                                    }
                                  }}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                            {expense.status !== 'approved' ? (
                              <Tooltip title={canApproveExpenses() ? "Approve" : "No approve permission"}>
                                <span>
                                  <IconButton 
                                    size="small"
                                    onClick={() => handleReviewExpense(expense)}
                                    disabled={!canApproveExpenses()}
                                    sx={{
                                      color: canApproveExpenses() ? 'success.main' : 'action.disabled',
                                      border: `1px solid ${alpha(theme.palette.grey[400], 0.3)}`,
                                      borderRadius: 1,
                                      transition: 'all 0.2s cubic-bezier(0.4, 0.0, 0.2, 1)',
                                      '&:hover': canApproveExpenses() ? {
                                        bgcolor: 'success.main',
                                        color: 'white',
                                        transform: 'scale(1.05)',
                                        boxShadow: `0 2px 8px ${alpha(theme.palette.success.main, 0.3)}`
                                      } : {},
                                      '&.Mui-disabled': {
                                        color: 'action.disabled',
                                        border: `1px solid ${alpha(theme.palette.grey[400], 0.2)}`
                                      }
                                    }}
                                  >
                                    <CheckCircleIcon fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            ) : (
                              <Tooltip title={canApproveExpenses() ? "Unapprove" : "No approve permission"}>
                                <span>
                                  <IconButton 
                                    size="small"
                                    onClick={() => handleUnapproveExpense(expense)}
                                    disabled={!canApproveExpenses()}
                                    sx={{
                                      color: canApproveExpenses() ? 'warning.main' : 'action.disabled',
                                      border: `1px solid ${alpha(theme.palette.grey[400], 0.3)}`,
                                      borderRadius: 1,
                                      transition: 'all 0.2s cubic-bezier(0.4, 0.0, 0.2, 1)',
                                      '&:hover': canApproveExpenses() ? {
                                        bgcolor: 'warning.main',
                                        color: 'white',
                                        transform: 'scale(1.05)',
                                        boxShadow: `0 2px 8px ${alpha(theme.palette.warning.main, 0.3)}`
                                      } : {},
                                      '&.Mui-disabled': {
                                        color: 'action.disabled',
                                        border: `1px solid ${alpha(theme.palette.grey[400], 0.2)}`
                                      }
                                    }}
                                  >
                                    <CancelIcon fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                    {filteredExpenses.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                          <Typography variant="body2" color="text.secondary">
                            No expenses yet. Click the + button to add the first expense.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'center', 
                mt: 3,
                mb: 4,
                px: 3
              }}>
                <Paper sx={{
                  borderRadius: 3,
                  border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                  background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.primary.main, 0.02)})`,
                  boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.15)}`,
                  overflow: 'hidden'
                }}>
                  <TablePagination
                    component="div"
                    count={filteredExpenses.length}
                    page={page}
                    onPageChange={(e, newPage) => setPage(newPage)}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={(e) => {
                      setRowsPerPage(parseInt(e.target.value, 10));
                      setPage(0);
                    }}
                    rowsPerPageOptions={[10, 25, 50]}
                  />
                </Paper>
              </Box>
            </Paper>

            {canEditExpenses() && (
              <Tooltip title="Add Expense">
                <Fab
                  color="primary"
                  onClick={() => handleOpenDialog()}
                  sx={{ 
                    position: 'fixed', 
                    bottom: 24, 
                    right: 24, 
                    width: 100, 
                    height: 100
                  }}
                >
                  <AddIcon sx={{ fontSize: 32 }} />
                </Fab>
              </Tooltip>
            )}

            {/* Add/Edit Dialog */}
            <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
              <DialogTitle>
                {editMode ? 'Edit Expense' : 'Add Expense'}
              </DialogTitle>
              <DialogContent>
                <Box sx={{ pt: 2 }}>
                  <Grid container spacing={3}>
                    <Grid item xs={12}>
                      <TextField
                        label="Description *"
                        value={formData.description}
                        onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                        fullWidth
                        required
                        multiline
                        rows={2}
                        autoFocus
                      />
                    </Grid>
                    
                    <Grid item xs={12} md={6}>
                      <DatePicker
                        label="Expense Date *"
                        value={formData.expense_date}
                        onChange={(newValue) => setFormData(prev => ({ ...prev, expense_date: newValue }))}
                        slotProps={{ textField: { fullWidth: true, required: true } }}
                      />
                    </Grid>
                    
                    <Grid item xs={12} md={6}>
                      <TextField
                        label="Total Amount *"
                        value={formData.total}
                        onChange={(e) => setFormData(prev => ({ ...prev, total: e.target.value }))}
                        fullWidth
                        required
                        type="number"
                        InputProps={{
                          startAdornment: <InputAdornment position="start">$</InputAdornment>,
                        }}
                        inputProps={{ step: "0.01", min: "0" }}
                      />
                    </Grid>
                    
                    <Grid item xs={12}>
                      <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block', fontWeight: 500 }}>
                          Account / Card *
                        </Typography>
                        <Button
                          fullWidth
                          variant="outlined"
                          onClick={handleOpenAccountSelectionDialog}
                          disabled={formData.paid_by === 'Employee (Reimburse)'}
                          sx={{
                            justifyContent: 'flex-start',
                            textAlign: 'left',
                            py: 1.5,
                            px: 2,
                            height: 56,
                            borderRadius: 2,
                            backgroundColor: '#f8f9fa',
                            border: `1px solid rgba(0,0,0,0.23)`,
                            color: (formData.paid_by === 'Employee (Reimburse)' || formData.account_id) ? 'text.primary' : 'text.secondary',
                            fontWeight: 400,
                            textTransform: 'none',
                            fontSize: '1rem',
                            '&:hover': {
                              border: `1px solid ${theme.palette.primary.main}`,
                              backgroundColor: '#f8f9fa',
                              boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`
                            },
                            '&.Mui-disabled': {
                              backgroundColor: '#f8f9fa',
                              color: 'text.primary',
                              border: `1px solid rgba(0,0,0,0.23)`
                            }
                          }}
                        >
                          {formData.paid_by === 'Employee (Reimburse)' ? 
                            'Employee Card' :
                            (formData.account_id ? 
                              (() => {
                                const account = accounts.find(a => a.id === formData.account_id);
                                return account ? `${account.account_name} (•••• ${account.last_four_digits})` : 'Select Account / Card';
                              })() :
                              'Select Account / Card'
                            )
                          }
                        </Button>
                      </Box>
                    </Grid>
                    
                    <Grid item xs={12} md={6}>
                      <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block', fontWeight: 500 }}>
                          Category *
                        </Typography>
                        <Button
                          fullWidth
                          variant="outlined"
                          onClick={handleOpenCategorySelectionDialog}
                          sx={{
                            justifyContent: 'flex-start',
                            textAlign: 'left',
                            py: 1.5,
                            px: 2,
                            height: 56,
                            borderRadius: 2,
                            backgroundColor: '#f8f9fa',
                            border: `1px solid rgba(0,0,0,0.23)`,
                            color: formData.category_id ? 'text.primary' : 'text.secondary',
                            fontWeight: 400,
                            textTransform: 'none',
                            fontSize: '1rem',
                            '&:hover': {
                              border: `1px solid ${theme.palette.primary.main}`,
                              backgroundColor: '#f8f9fa',
                              boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`
                            }
                          }}
                        >
                          {formData.category_id ? 
                            (() => {
                              const category = categories.find(c => c.id === formData.category_id);
                              return category ? category.name : 'Select Category';
                            })() :
                            'Select Category'
                          }
                        </Button>
                      </Box>
                    </Grid>
                    
                    <Grid item xs={12} md={6}>
                      <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block', fontWeight: 500 }}>
                          Paid By *
                        </Typography>
                        <FormControl fullWidth>
                          <Select
                            value={formData.paid_by}
                            onChange={(e) => {
                              const newValue = e.target.value;
                              setFormData(prev => {
                                // If Employee (Reimburse) is selected, clear account_id
                                if (newValue === 'Employee (Reimburse)') {
                                  return { ...prev, paid_by: newValue, account_id: '' };
                                }
                                return { ...prev, paid_by: newValue };
                              });
                            }}
                            displayEmpty
                            sx={{
                              height: 56,
                              backgroundColor: '#f8f9fa'
                            }}
                          >
                            <MenuItem value="">
                              <em style={{ color: '#999' }}>Select Payment Method</em>
                            </MenuItem>
                            {PAID_BY_OPTIONS.map((option) => (
                              <MenuItem key={option.value} value={option.value}>
                                {option.label}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Box>
                    </Grid>
                    
                    <Grid item xs={12}>
                      <Divider sx={{ my: 1 }} />
                      <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 500 }}>
                        Receipt/Document
                      </Typography>
                      {formData.document_file_name ? (
                        <Card sx={{ p: 2, bgcolor: alpha(theme.palette.primary.main, 0.05) }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <DescriptionIcon sx={{ color: 'primary.main' }} />
                            <Box sx={{ flex: 1 }}>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {formData.document_file_name}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {formData.document_file_size ? `${(formData.document_file_size / 1024).toFixed(2)} KB` : ''}
                              </Typography>
                            </Box>
                            <Button
                              size="small"
                              startIcon={<VisibilityIcon />}
                              onClick={() => viewDocumentFile(formData.document_file_path, formData.document_file_name)}
                            >
                              View
                            </Button>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={removeDocumentFile}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Box>
                        </Card>
                      ) : (
                        <Button
                          component="label"
                          variant="outlined"
                          startIcon={<CloudUploadIcon />}
                          fullWidth
                          sx={{ py: 2 }}
                        >
                          Upload Receipt/Document
                          <input
                            type="file"
                            hidden
                            accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                            onChange={handleFileUpload}
                          />
                        </Button>
                      )}
                      {documentFile && !formData.document_file_name && (
                        <Box sx={{ mt: 2, p: 2, bgcolor: alpha(theme.palette.success.main, 0.1), borderRadius: 1 }}>
                          <Typography variant="body2" color="success.main" sx={{ fontWeight: 500 }}>
                            {documentFile.name} ({(documentFile.size / 1024).toFixed(2)} KB) - Ready to upload
                          </Typography>
                        </Box>
                      )}
                    </Grid>
                  </Grid>
                </Box>
              </DialogContent>
              <DialogActions>
                <Button onClick={handleCloseDialog}>Cancel</Button>
                <Button 
                  onClick={handleSubmit} 
                  variant="contained" 
                  disabled={
                    submitting || 
                    uploadingDocument ||
                    !formData.description?.trim() ||
                    !formData.expense_date ||
                    !formData.total ||
                    parseFloat(formData.total) <= 0 ||
                    !formData.category_id ||
                    !formData.paid_by ||
                    (formData.paid_by !== 'Employee (Reimburse)' && !formData.account_id)
                  }
                >
                  {uploadingDocument ? 'Uploading...' : submitting ? 'Saving...' : editMode ? 'Update' : 'Add'}
                </Button>
              </DialogActions>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
              <DialogTitle>Confirm Delete</DialogTitle>
              <DialogContent>
                <Typography>Are you sure you want to delete this expense?</Typography>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
                <Button onClick={handleConfirmDelete} variant="contained" color="error">
                  Delete
                </Button>
              </DialogActions>
            </Dialog>

            {/* View Details Dialog - Continues in next message due to length */}
            <Dialog
              open={viewDialogOpen}
              onClose={() => setViewDialogOpen(false)}
              maxWidth="md"
              fullWidth
            >
              <DialogTitle sx={{ 
                fontWeight: 500,
                color: 'primary.main',
                borderBottom: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)}, ${alpha(theme.palette.primary.main, 0.03)})`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 2
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <VisibilityIcon sx={{ color: 'primary.main' }} />
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 500 }}>
                      Expense Details
                    </Typography>
                  </Box>
                </Box>
                <IconButton onClick={() => setViewDialogOpen(false)} size="small">
                  <CloseIcon />
                </IconButton>
              </DialogTitle>
              
              <DialogContent sx={{ mt: 3 }}>
                {viewExpense && (
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, display: 'block', mb: 0.5 }}>
                        Employee
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {viewExpense.employee?.full_name || '—'}
                      </Typography>
                    </Grid>
                    
                    <Grid item xs={12} md={6}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, display: 'block', mb: 0.5 }}>
                        Date
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {formatDate(viewExpense.expense_date)}
                      </Typography>
                    </Grid>
                    
                    <Grid item xs={12}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, display: 'block', mb: 0.5 }}>
                        Description
                      </Typography>
                      <Typography variant="body1">
                        {viewExpense.description}
                      </Typography>
                    </Grid>
                    
                    <Grid item xs={12} md={6}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, display: 'block', mb: 0.5 }}>
                        Category
                      </Typography>
                      {viewExpense.category ? (
                        <Chip 
                          label={viewExpense.category.name} 
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      ) : '—'}
                    </Grid>
                    
                    <Grid item xs={12} md={6}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, display: 'block', mb: 0.5 }}>
                        Paid By
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {viewExpense.paid_by || '—'}
                      </Typography>
                    </Grid>
                    
                    <Grid item xs={12} md={6}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, display: 'block', mb: 0.5 }}>
                        Account / Card
                      </Typography>
                      {viewExpense.account ? (
                        <Typography variant="body1" sx={{ fontFamily: 'monospace', fontWeight: 500 }}>
                          {viewExpense.account.account_name} (•••• {viewExpense.account.last_four_digits})
                        </Typography>
                      ) : '—'}
                    </Grid>
                    
                    <Grid item xs={12} md={6}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, display: 'block', mb: 0.5 }}>
                        Total Amount
                      </Typography>
                      <Typography variant="h5" sx={{ fontWeight: 500, color: 'primary.main' }}>
                        {formatCurrency(viewExpense.total)}
                      </Typography>
                    </Grid>
                    
                    {viewExpense.document_file_name && (
                      <>
                        <Grid item xs={12}>
                          <Divider sx={{ my: 1 }} />
                          <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 500 }}>
                            Receipt/Document
                          </Typography>
                        </Grid>
                        <Grid item xs={12}>
                          <Card sx={{ p: 2, bgcolor: alpha(theme.palette.primary.main, 0.05) }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                              <DescriptionIcon sx={{ color: 'primary.main', fontSize: 40 }} />
                              <Box sx={{ flex: 1 }}>
                                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                  {viewExpense.document_file_name}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {viewExpense.document_file_size ? `${(viewExpense.document_file_size / 1024).toFixed(2)} KB` : ''}
                                </Typography>
                              </Box>
                              <Button
                                variant="contained"
                                size="small"
                                startIcon={<VisibilityIcon />}
                                onClick={() => viewDocumentFile(viewExpense.document_file_path, viewExpense.document_file_name)}
                              >
                                View
                              </Button>
                            </Box>
                          </Card>
                        </Grid>
                      </>
                    )}
                    
                    {viewExpense.status === 'approved' && viewExpense.approved_by_signature && (
                      <>
                        <Grid item xs={12}>
                          <Divider sx={{ my: 1 }} />
                          <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 500, color: 'success.main' }}>
                            Approval Information
                          </Typography>
                        </Grid>
                        <Grid item xs={12}>
                          <Card sx={{ 
                            p: 3, 
                            border: `1px solid ${theme.palette.success.main}`, 
                            borderRadius: 2, 
                            backgroundColor: alpha(theme.palette.success.main, 0.05)
                          }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 500, mb: 2, color: 'success.main', display: 'flex', alignItems: 'center', gap: 1 }}>
                              <VerifiedIcon />
                              Approved By
                            </Typography>
                            <Box sx={{ 
                              border: `1px solid ${theme.palette.success.main}`, 
                              p: 2, 
                              borderRadius: 1, 
                              bgcolor: 'white',
                              mb: 2
                            }}>
                              <Box sx={{ 
                                fontFamily: "'Brush Script MT', 'Brush Script Std', cursive", 
                                fontSize: '24px', 
                                color: theme.palette.text.primary
                              }}>
                                {viewExpense.approved_by_signature}
                              </Box>
                            </Box>
                            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                              Date: {formatDate(viewExpense.approved_at)}
                            </Typography>
                            {viewExpense.approval_notes && (
                              <Box sx={{ mt: 2 }}>
                                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, display: 'block', mb: 0.5 }}>
                                  Approval Notes:
                                </Typography>
                                <Typography variant="body2">{viewExpense.approval_notes}</Typography>
                              </Box>
                            )}
                          </Card>
                        </Grid>
                      </>
                    )}
                  </Grid>
                )}
              </DialogContent>
              
              <DialogActions sx={{ p: 3, gap: 1 }}>
                <Button onClick={() => setViewDialogOpen(false)} variant="outlined">
                  Close
                </Button>
                {canEditExpenses() && (
                  <Button 
                    onClick={() => {
                      setViewDialogOpen(false);
                      handleOpenDialog(viewExpense);
                    }}
                    variant="contained"
                    startIcon={<EditIcon />}
                  >
                    Edit Expense
                  </Button>
                )}
              </DialogActions>
            </Dialog>

            {/* Approval Dialog */}
            <Dialog 
              open={approvalDialogOpen} 
              onClose={() => setApprovalDialogOpen(false)}
              maxWidth="sm"
              fullWidth
              PaperProps={{
                sx: {
                  borderRadius: 3,
                  border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                  boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.2)}`
                }
              }}
            >
              <DialogTitle sx={{ 
                fontWeight: 500,
                fontSize: '1.2rem',
                color: 'primary.main',
                borderBottom: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                background: alpha(theme.palette.primary.main, 0.05),
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                p: 3
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <CheckCircleIcon sx={{ color: 'success.main' }} />
                  <Typography variant="h5" sx={{ fontWeight: 500, color: 'primary.main' }}>
                    Approve Expense
                  </Typography>
                </Box>
                <IconButton 
                  onClick={() => setApprovalDialogOpen(false)} 
                  size="small"
                  sx={{
                    borderRadius: 2,
                    border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                    '&:hover': {
                      transform: 'scale(1.1)',
                      border: `1px solid ${theme.palette.primary.main}`,
                      boxShadow: `0 2px 8px ${alpha(theme.palette.grey[900], 0.2)}`
                    }
                  }}
                >
                  <CloseIcon />
                </IconButton>
              </DialogTitle>
              
              <DialogContent sx={{ 
                backgroundColor: alpha(theme.palette.grey[50], 0.5), 
                p: 3,
                mt: 4
              }}>
                {approvalExpense && (
                  <Grid container spacing={3}>
                    <Grid item xs={12}>
                      <Card sx={{ 
                        p: 3, 
                        border: `1px solid ${theme.palette.grey[400]}`, 
                        borderRadius: 3, 
                        backgroundColor: 'white',
                        boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.08)}`
                      }}>
                        <Typography variant="h6" sx={{ fontWeight: 500, mb: 2, color: theme.palette.text.primary }}>
                          Approval Signature
                        </Typography>
                        <Grid container spacing={3}>
                          <Grid item xs={12}>
                            <TextField
                              fullWidth
                              label="Your Name (Electronic Signature) *"
                              value={signatureName}
                              onChange={(e) => setSignatureName(e.target.value)}
                              required
                              placeholder="Enter your full name to approve this expense"
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  borderRadius: 2
                                }
                              }}
                            />
                          </Grid>
                          
                          {signatureName && (
                            <Grid item xs={12}>
                              <Box sx={{ 
                                border: `1px solid ${theme.palette.primary.main}`, 
                                p: 3, 
                                borderRadius: 2, 
                                bgcolor: alpha(theme.palette.primary.main, 0.05)
                              }}>
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1, fontWeight: 500 }}>
                                  ELECTRONIC SIGNATURE PREVIEW:
                                </Typography>
                                <Box sx={{ 
                                  fontFamily: "'Brush Script MT', 'Brush Script Std', cursive", 
                                  fontSize: '24px', 
                                  color: theme.palette.text.primary,
                                  textShadow: '1px 1px 1px rgba(0,0,0,0.1)',
                                  mb: 1
                                }}>
                                  {signatureName}
                                </Box>
                                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                                  Signed: {dayjs().format('MMM DD, YYYY HH:mm')}
                                </Typography>
                              </Box>
                            </Grid>
                          )}

                          <Grid item xs={12}>
                            <TextField
                              fullWidth
                              multiline
                              rows={3}
                              label="Approval Notes (Optional)"
                              value={approvalNotes}
                              onChange={(e) => setApprovalNotes(e.target.value)}
                              placeholder="Add any notes or comments about this approval..."
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  borderRadius: 2
                                }
                              }}
                            />
                          </Grid>
                        </Grid>
                      </Card>
                    </Grid>
                  </Grid>
                )}
              </DialogContent>
              
              <DialogActions sx={{ 
                p: 3, 
                gap: 2, 
                borderTop: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)}, ${alpha(theme.palette.primary.main, 0.04)})`
              }}>
                <Button 
                  onClick={() => setApprovalDialogOpen(false)}
                  startIcon={<CloseIcon />}
                  sx={{
                    borderRadius: 2,
                    px: 3,
                    py: 1.5,
                    color: theme.palette.text.secondary,
                    fontWeight: 500,
                    background: 'rgba(255, 255, 255, 0.8)',
                    '&:hover': {
                      backgroundColor: alpha(theme.palette.primary.main, 0.04)
                    }
                  }}
                >
                  Cancel
                </Button>
                
                <Button 
                  onClick={handleApprovalSubmit}
                  variant="contained"
                  startIcon={<CheckCircleIcon />}
                  disabled={!signatureName.trim()}
                  sx={{
                    borderRadius: 2,
                    px: 4,
                    py: 1.5,
                    background: `linear-gradient(135deg, ${theme.palette.success.main}, ${theme.palette.success.dark})`,
                    color: 'white',
                    fontWeight: 500,
                    boxShadow: `0 4px 12px ${alpha(theme.palette.success.main, 0.3)}`,
                    '&:hover': {
                      background: `linear-gradient(135deg, ${theme.palette.success.dark}, ${theme.palette.success.main})`,
                      boxShadow: `0 6px 20px ${alpha(theme.palette.success.main, 0.4)}`
                    },
                    '&:disabled': {
                      background: theme.palette.grey[400],
                      color: theme.palette.grey[100]
                    }
                  }}
                >
                  Approve
                </Button>
              </DialogActions>
            </Dialog>

            {/* Filters Dialog */}
            <Dialog open={filterDialogOpen} onClose={() => setFilterDialogOpen(false)} maxWidth="sm" fullWidth>
              <DialogTitle>Filter Expenses</DialogTitle>
              <DialogContent>
                <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <DatePicker
                    label="Date From"
                    value={filters.dateFrom}
                    onChange={(newValue) => setFilters(prev => ({ ...prev, dateFrom: newValue }))}
                    slotProps={{ textField: { fullWidth: true } }}
                  />
                  
                  <DatePicker
                    label="Date To"
                    value={filters.dateTo}
                    onChange={(newValue) => setFilters(prev => ({ ...prev, dateTo: newValue }))}
                    slotProps={{ textField: { fullWidth: true } }}
                  />
                  
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block', fontWeight: 500 }}>
                      Category
                    </Typography>
                    <Button
                      fullWidth
                      variant="outlined"
                      onClick={handleOpenFilterCategorySelectionDialog}
                      sx={{
                        justifyContent: 'flex-start',
                        textAlign: 'left',
                        py: 1.5,
                        px: 2,
                        height: 56,
                        borderRadius: 2,
                        backgroundColor: '#f8f9fa',
                        border: `1px solid rgba(0,0,0,0.23)`,
                        color: filters.category ? 'text.primary' : 'text.secondary',
                        fontWeight: 400,
                        textTransform: 'none',
                        fontSize: '1rem',
                        '&:hover': {
                          border: `1px solid ${theme.palette.primary.main}`,
                          backgroundColor: '#f8f9fa',
                          boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`
                        }
                      }}
                    >
                      {filters.category ? 
                        (() => {
                          const category = categories.find(c => c.id === filters.category);
                          return category ? category.name : 'All Categories';
                        })() :
                        'All Categories'
                      }
                    </Button>
                  </Box>
                  
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block', fontWeight: 500 }}>
                      Employee
                    </Typography>
                    <Button
                      fullWidth
                      variant="outlined"
                      onClick={handleOpenFilterEmployeeSelectionDialog}
                      sx={{
                        justifyContent: 'flex-start',
                        textAlign: 'left',
                        py: 1.5,
                        px: 2,
                        height: 56,
                        borderRadius: 2,
                        backgroundColor: '#f8f9fa',
                        border: `1px solid rgba(0,0,0,0.23)`,
                        color: filters.employee ? 'text.primary' : 'text.secondary',
                        fontWeight: 400,
                        textTransform: 'none',
                        fontSize: '1rem',
                        '&:hover': {
                          border: `1px solid ${theme.palette.primary.main}`,
                          backgroundColor: '#f8f9fa',
                          boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`
                        }
                      }}
                    >
                      {filters.employee ? 
                        (() => {
                          const employee = employees.find(e => e.id === filters.employee);
                          return employee ? employee.full_name : 'All Employees';
                        })() :
                        'All Employees'
                      }
                    </Button>
                  </Box>
                  
                  <FormControl fullWidth>
                    <InputLabel>Payment Method</InputLabel>
                    <Select
                      value={filters.paidBy}
                      label="Payment Method"
                      onChange={(e) => setFilters(prev => ({ ...prev, paidBy: e.target.value }))}
                    >
                      <MenuItem value="">
                        <em>All Methods</em>
                      </MenuItem>
                      {PAID_BY_OPTIONS.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              </DialogContent>
              <DialogActions>
                <Button onClick={clearFilters}>Clear All</Button>
                <Button onClick={() => setFilterDialogOpen(false)} variant="contained">
                  Apply
                </Button>
              </DialogActions>
            </Dialog>

            {/* Filter Category Selection Dialog */}
            <Dialog
              open={filterCategorySelectionDialogOpen}
              onClose={handleCloseFilterCategorySelectionDialog}
              fullWidth
              maxWidth="md"
              PaperProps={{
                sx: {
                  borderRadius: 4,
                  height: '40vh',
                  width: '600px',
                  maxWidth: '600px',
                  margin: 'auto',
                  border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                  boxShadow: '0 24px 48px rgba(0,0,0,0.15)'
                }
              }}
            >
              <DialogTitle sx={{ 
                pb: 2, 
                fontSize: '1.25rem', 
                fontWeight: 500,
                color: 'text.primary',
                borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}>
                <Typography variant="h6" component="span" sx={{ fontWeight: 500, fontSize: '1.1rem' }}>
                  Select Category
                </Typography>
                <IconButton onClick={handleCloseFilterCategorySelectionDialog} size="small">
                  <CloseIcon />
                </IconButton>
              </DialogTitle>
              <DialogContent sx={{ p: 0 }}>
                <Box sx={{ p: 3, borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.8)}` }}>
                  <TextField
                    fullWidth
                    placeholder="Search categories..."
                    value={filterCategorySearchTerm}
                    onChange={(e) => setFilterCategorySearchTerm(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon sx={{ color: theme.palette.primary.main }} />
                        </InputAdornment>
                      ),
                      sx: { 
                        borderRadius: 3,
                        border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                        background: 'white',
                        '&:hover': {
                          border: `1px solid ${theme.palette.primary.main}`,
                          boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`
                        },
                        '&.Mui-focused': {
                          border: `1px solid ${theme.palette.primary.main}`,
                          boxShadow: `0 4px 16px ${alpha(theme.palette.primary.main, 0.25)}`
                        }
                      }
                    }}
                    size="medium"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': {
                          border: 'none'
                        },
                        '&:hover fieldset': {
                          border: 'none'
                        },
                        '&.Mui-focused fieldset': {
                          border: 'none'
                        }
                      }
                    }}
                  />
                </Box>
                
                <Box sx={{ 
                  flex: 1,
                  overflowY: 'auto',
                  '&::-webkit-scrollbar': {
                    display: 'none'
                  },
                  scrollbarWidth: 'none',
                  msOverflowStyle: 'none'
                }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 500, py: 2, pl: 3 }}>Category</TableCell>
                        <TableCell sx={{ fontWeight: 500, py: 2 }} align="center">Action</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {getFilteredCategoriesForFilterSelection().map((category) => (
                        <TableRow 
                          key={category.id}
                          hover
                          sx={{
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            '&:hover': {
                              bgcolor: 'grey.50',
                              transform: 'translateX(4px)',
                              boxShadow: 'inset 4px 0 0 black'
                            },
                            '& .MuiTableCell-root': {
                              borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.6)}`,
                              py: 2,
                              pl: 3,
                              pr: 3
                            }
                          }}
                        >
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.9rem' }}>
                              {category.name}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => handleFilterDialogCategorySelect(category)}
                              sx={{
                                borderRadius: 2,
                                textTransform: 'none',
                                fontWeight: 500,
                                fontSize: '0.8rem',
                                px: 2,
                                py: 1,
                                border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                                transition: 'all 0.2s cubic-bezier(0.4, 0.0, 0.2, 1)',
                                bgcolor: filterDialogSelectedCategory?.id === category.id ? 'black' : 'transparent',
                                color: filterDialogSelectedCategory?.id === category.id ? 'white' : 'inherit',
                                '&:hover': {
                                  bgcolor: filterDialogSelectedCategory?.id === category.id ? 'black' : 'transparent',
                                  border: `1px solid ${theme.palette.primary.main}`,
                                  transform: 'translateY(-1px)',
                                  boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.2)}`
                                }
                              }}
                            >
                              Select
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                      {getFilteredCategoriesForFilterSelection().length === 0 && (
                        <TableRow>
                          <TableCell colSpan={2} sx={{ textAlign: 'center', py: 4 }}>
                            <Typography variant="body2" color="text.secondary">
                              {filterCategorySearchTerm ? 'No categories found matching your search.' : 'No categories available.'}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </Box>
              </DialogContent>
              <DialogActions sx={{ 
                p: 3, 
                gap: 2, 
                borderTop: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`,
                bgcolor: alpha(theme.palette.grey[50], 0.5),
                display: 'flex',
                justifyContent: 'flex-end'
              }}>
                <Button
                  onClick={handleCloseFilterCategorySelectionDialog}
                  variant="outlined"
                  sx={{ 
                    borderRadius: 2, 
                    px: 3, 
                    py: 1, 
                    fontSize: '0.9rem', 
                    fontWeight: 500,
                    textTransform: 'none',
                    border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                    '&:hover': {
                      border: `1px solid ${theme.palette.grey[600]}`,
                      transform: 'translateY(-1px)'
                    }
                  }}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleConfirmFilterCategorySelection}
                  variant="contained"
                  disabled={!filterDialogSelectedCategory}
                  sx={{ 
                    borderRadius: 2, 
                    px: 3, 
                    py: 1, 
                    fontSize: '0.9rem', 
                    fontWeight: 500,
                    textTransform: 'none',
                    background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                    '&:hover': {
                      background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
                      transform: 'translateY(-1px)',
                      boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.3)}`
                    },
                    '&:disabled': {
                      background: alpha(theme.palette.grey[400], 0.5),
                      color: alpha(theme.palette.grey[600], 0.7)
                    }
                  }}
                >
                  Confirm Selection
                </Button>
              </DialogActions>
            </Dialog>

            {/* Filter Employee Selection Dialog */}
            <Dialog
              open={filterEmployeeSelectionDialogOpen}
              onClose={handleCloseFilterEmployeeSelectionDialog}
              fullWidth
              maxWidth="md"
              PaperProps={{
                sx: {
                  borderRadius: 4,
                  height: '40vh',
                  width: '600px',
                  maxWidth: '600px',
                  margin: 'auto',
                  border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                  boxShadow: '0 24px 48px rgba(0,0,0,0.15)'
                }
              }}
            >
              <DialogTitle sx={{ 
                pb: 2, 
                fontSize: '1.25rem', 
                fontWeight: 500,
                color: 'text.primary',
                borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}>
                <Typography variant="h6" component="span" sx={{ fontWeight: 500, fontSize: '1.1rem' }}>
                  Select Employee
                </Typography>
                <IconButton onClick={handleCloseFilterEmployeeSelectionDialog} size="small">
                  <CloseIcon />
                </IconButton>
              </DialogTitle>
              <DialogContent sx={{ p: 0 }}>
                <Box sx={{ p: 3, borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.8)}` }}>
                  <TextField
                    fullWidth
                    placeholder="Search employees..."
                    value={filterEmployeeSearchTerm}
                    onChange={(e) => setFilterEmployeeSearchTerm(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon sx={{ color: theme.palette.primary.main }} />
                        </InputAdornment>
                      ),
                      sx: { 
                        borderRadius: 3,
                        border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                        background: 'white',
                        '&:hover': {
                          border: `1px solid ${theme.palette.primary.main}`,
                          boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`
                        },
                        '&.Mui-focused': {
                          border: `1px solid ${theme.palette.primary.main}`,
                          boxShadow: `0 4px 16px ${alpha(theme.palette.primary.main, 0.25)}`
                        }
                      }
                    }}
                    size="medium"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': {
                          border: 'none'
                        },
                        '&:hover fieldset': {
                          border: 'none'
                        },
                        '&.Mui-focused fieldset': {
                          border: 'none'
                        }
                      }
                    }}
                  />
                </Box>
                
                <Box sx={{ 
                  flex: 1,
                  overflowY: 'auto',
                  '&::-webkit-scrollbar': {
                    display: 'none'
                  },
                  scrollbarWidth: 'none',
                  msOverflowStyle: 'none'
                }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 500, py: 2, pl: 3 }}>Employee Name</TableCell>
                        <TableCell sx={{ fontWeight: 500, py: 2 }} align="center">Action</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {getFilteredEmployeesForFilterSelection().map((employee) => (
                        <TableRow 
                          key={employee.id}
                          hover
                          sx={{
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            '&:hover': {
                              bgcolor: 'grey.50',
                              transform: 'translateX(4px)',
                              boxShadow: 'inset 4px 0 0 black'
                            },
                            '& .MuiTableCell-root': {
                              borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.6)}`,
                              py: 2,
                              pl: 3,
                              pr: 3
                            }
                          }}
                        >
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.9rem' }}>
                              {employee.full_name}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => handleFilterDialogEmployeeSelect(employee)}
                              sx={{
                                borderRadius: 2,
                                textTransform: 'none',
                                fontWeight: 500,
                                fontSize: '0.8rem',
                                px: 2,
                                py: 1,
                                border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                                transition: 'all 0.2s cubic-bezier(0.4, 0.0, 0.2, 1)',
                                bgcolor: filterDialogSelectedEmployee?.id === employee.id ? 'black' : 'transparent',
                                color: filterDialogSelectedEmployee?.id === employee.id ? 'white' : 'inherit',
                                '&:hover': {
                                  bgcolor: filterDialogSelectedEmployee?.id === employee.id ? 'black' : 'transparent',
                                  border: `1px solid ${theme.palette.primary.main}`,
                                  transform: 'translateY(-1px)',
                                  boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.2)}`
                                }
                              }}
                            >
                              Select
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                      {getFilteredEmployeesForFilterSelection().length === 0 && (
                        <TableRow>
                          <TableCell colSpan={2} sx={{ textAlign: 'center', py: 4 }}>
                            <Typography variant="body2" color="text.secondary">
                              {filterEmployeeSearchTerm ? 'No employees found matching your search.' : 'No employees available.'}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </Box>
              </DialogContent>
              <DialogActions sx={{ 
                p: 3, 
                gap: 2, 
                borderTop: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`,
                bgcolor: alpha(theme.palette.grey[50], 0.5),
                display: 'flex',
                justifyContent: 'flex-end'
              }}>
                <Button
                  onClick={handleCloseFilterEmployeeSelectionDialog}
                  variant="outlined"
                  sx={{ 
                    borderRadius: 2, 
                    px: 3, 
                    py: 1, 
                    fontSize: '0.9rem', 
                    fontWeight: 500,
                    textTransform: 'none',
                    border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                    '&:hover': {
                      border: `1px solid ${theme.palette.grey[600]}`,
                      transform: 'translateY(-1px)'
                    }
                  }}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleConfirmFilterEmployeeSelection}
                  variant="contained"
                  disabled={!filterDialogSelectedEmployee}
                  sx={{ 
                    borderRadius: 2, 
                    px: 3, 
                    py: 1, 
                    fontSize: '0.9rem', 
                    fontWeight: 500,
                    textTransform: 'none',
                    background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                    '&:hover': {
                      background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
                      transform: 'translateY(-1px)',
                      boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.3)}`
                    },
                    '&:disabled': {
                      background: alpha(theme.palette.grey[400], 0.5),
                      color: alpha(theme.palette.grey[600], 0.7)
                    }
                  }}
                >
                  Confirm Selection
                </Button>
              </DialogActions>
            </Dialog>

            {/* Account Selection Dialog */}
            <Dialog
              open={accountSelectionDialogOpen}
              onClose={handleCloseAccountSelectionDialog}
              fullWidth
              maxWidth="md"
              PaperProps={{
                sx: {
                  borderRadius: 4,
                  height: '40vh',
                  width: '600px',
                  maxWidth: '600px',
                  margin: 'auto',
                  border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                  boxShadow: '0 24px 48px rgba(0,0,0,0.15)'
                }
              }}
            >
              <DialogTitle sx={{ 
                pb: 2, 
                fontSize: '1.25rem', 
                fontWeight: 500,
                color: 'text.primary',
                borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}>
                <Typography variant="h6" component="span" sx={{ fontWeight: 500, fontSize: '1.1rem' }}>
                  Select Account / Card
                </Typography>
                <IconButton onClick={handleCloseAccountSelectionDialog} size="small">
                  <CloseIcon />
                </IconButton>
              </DialogTitle>
              <DialogContent sx={{ p: 0 }}>
                <Box sx={{ p: 3, borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.8)}` }}>
                  <TextField
                    fullWidth
                    placeholder="Search accounts..."
                    value={accountSearchTerm}
                    onChange={(e) => setAccountSearchTerm(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon sx={{ color: theme.palette.primary.main }} />
                        </InputAdornment>
                      ),
                      sx: { 
                        borderRadius: 3,
                        border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                        background: 'white',
                        '&:hover': {
                          border: `1px solid ${theme.palette.primary.main}`,
                          boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`
                        },
                        '&.Mui-focused': {
                          border: `1px solid ${theme.palette.primary.main}`,
                          boxShadow: `0 4px 16px ${alpha(theme.palette.primary.main, 0.25)}`
                        }
                      }
                    }}
                    size="medium"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': {
                          border: 'none'
                        },
                        '&:hover fieldset': {
                          border: 'none'
                        },
                        '&.Mui-focused fieldset': {
                          border: 'none'
                        }
                      }
                    }}
                  />
                </Box>
                
                <Box sx={{ 
                  flex: 1,
                  overflowY: 'auto',
                  '&::-webkit-scrollbar': {
                    display: 'none'
                  },
                  scrollbarWidth: 'none',
                  msOverflowStyle: 'none'
                }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 500, py: 2, pl: 3 }}>Account Name</TableCell>
                        <TableCell sx={{ fontWeight: 500, py: 2 }}>Card Number</TableCell>
                        <TableCell sx={{ fontWeight: 500, py: 2 }} align="center">Action</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {getFilteredAccountsForSelection().map((account) => (
                        <TableRow 
                          key={account.id}
                          hover
                          sx={{
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            '&:hover': {
                              bgcolor: 'grey.50',
                              transform: 'translateX(4px)',
                              boxShadow: 'inset 4px 0 0 black'
                            },
                            '& .MuiTableCell-root': {
                              borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.6)}`,
                              py: 2,
                              pl: 3,
                              pr: 3
                            }
                          }}
                        >
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.9rem' }}>
                              {account.account_name}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.9rem' }}>
                              •••• {account.last_four_digits}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => handleDialogAccountSelect(account)}
                              sx={{
                                borderRadius: 2,
                                textTransform: 'none',
                                fontWeight: 500,
                                fontSize: '0.8rem',
                                px: 2,
                                py: 1,
                                border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                                transition: 'all 0.2s cubic-bezier(0.4, 0.0, 0.2, 1)',
                                bgcolor: dialogSelectedAccount?.id === account.id ? 'black' : 'transparent',
                                color: dialogSelectedAccount?.id === account.id ? 'white' : 'inherit',
                                '&:hover': {
                                  bgcolor: dialogSelectedAccount?.id === account.id ? 'black' : 'transparent',
                                  border: `1px solid ${theme.palette.primary.main}`,
                                  transform: 'translateY(-1px)',
                                  boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.2)}`
                                }
                              }}
                            >
                              Select
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                      {getFilteredAccountsForSelection().length === 0 && (
                        <TableRow>
                          <TableCell colSpan={3} sx={{ textAlign: 'center', py: 4 }}>
                            <Typography variant="body2" color="text.secondary">
                              {accountSearchTerm ? 'No accounts found matching your search.' : 'No accounts available.'}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </Box>
              </DialogContent>
              <DialogActions sx={{ 
                p: 3, 
                gap: 2, 
                borderTop: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`,
                bgcolor: alpha(theme.palette.grey[50], 0.5),
                display: 'flex',
                justifyContent: 'flex-end'
              }}>
                <Button
                  onClick={handleCloseAccountSelectionDialog}
                  variant="outlined"
                  sx={{ 
                    borderRadius: 2, 
                    px: 3, 
                    py: 1, 
                    fontSize: '0.9rem', 
                    fontWeight: 500,
                    textTransform: 'none',
                    border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                    '&:hover': {
                      border: `1px solid ${theme.palette.grey[600]}`,
                      transform: 'translateY(-1px)'
                    }
                  }}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleConfirmAccountSelection}
                  variant="contained"
                  disabled={!dialogSelectedAccount}
                  sx={{ 
                    borderRadius: 2, 
                    px: 3, 
                    py: 1, 
                    fontSize: '0.9rem', 
                    fontWeight: 500,
                    textTransform: 'none',
                    background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                    '&:hover': {
                      background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
                      transform: 'translateY(-1px)',
                      boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.3)}`
                    },
                    '&:disabled': {
                      background: alpha(theme.palette.grey[400], 0.5),
                      color: alpha(theme.palette.grey[600], 0.7)
                    }
                  }}
                >
                  Confirm Selection
                </Button>
              </DialogActions>
            </Dialog>

            {/* Category Selection Dialog */}
            <Dialog
              open={categorySelectionDialogOpen}
              onClose={handleCloseCategorySelectionDialog}
              fullWidth
              maxWidth="md"
              PaperProps={{
                sx: {
                  borderRadius: 4,
                  height: '40vh',
                  width: '600px',
                  maxWidth: '600px',
                  margin: 'auto',
                  border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                  boxShadow: '0 24px 48px rgba(0,0,0,0.15)'
                }
              }}
            >
              <DialogTitle sx={{ 
                pb: 2, 
                fontSize: '1.25rem', 
                fontWeight: 500,
                color: 'text.primary',
                borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}>
                <Typography variant="h6" component="span" sx={{ fontWeight: 500, fontSize: '1.1rem' }}>
                  Select Category
                </Typography>
                <IconButton onClick={handleCloseCategorySelectionDialog} size="small">
                  <CloseIcon />
                </IconButton>
              </DialogTitle>
              <DialogContent sx={{ p: 0 }}>
                <Box sx={{ p: 3, borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.8)}` }}>
                  <TextField
                    fullWidth
                    placeholder="Search categories..."
                    value={categorySearchTerm}
                    onChange={(e) => setCategorySearchTerm(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon sx={{ color: theme.palette.primary.main }} />
                        </InputAdornment>
                      ),
                      sx: { 
                        borderRadius: 3,
                        border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                        background: 'white',
                        '&:hover': {
                          border: `1px solid ${theme.palette.primary.main}`,
                          boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`
                        },
                        '&.Mui-focused': {
                          border: `1px solid ${theme.palette.primary.main}`,
                          boxShadow: `0 4px 16px ${alpha(theme.palette.primary.main, 0.25)}`
                        }
                      }
                    }}
                    size="medium"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': {
                          border: 'none'
                        },
                        '&:hover fieldset': {
                          border: 'none'
                        },
                        '&.Mui-focused fieldset': {
                          border: 'none'
                        }
                      }
                    }}
                  />
                </Box>
                
                <Box sx={{ 
                  flex: 1,
                  overflowY: 'auto',
                  '&::-webkit-scrollbar': {
                    display: 'none'
                  },
                  scrollbarWidth: 'none',
                  msOverflowStyle: 'none'
                }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 500, py: 2, pl: 3 }}>Category</TableCell>
                        <TableCell sx={{ fontWeight: 500, py: 2 }} align="center">Action</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {getFilteredCategoriesForSelection().map((category) => (
                        <TableRow 
                          key={category.id}
                          hover
                          sx={{
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            '&:hover': {
                              bgcolor: 'grey.50',
                              transform: 'translateX(4px)',
                              boxShadow: 'inset 4px 0 0 black'
                            },
                            '& .MuiTableCell-root': {
                              borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.6)}`,
                              py: 2,
                              pl: 3,
                              pr: 3
                            }
                          }}
                        >
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.9rem' }}>
                              {category.name}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => handleDialogCategorySelect(category)}
                              sx={{
                                borderRadius: 2,
                                textTransform: 'none',
                                fontWeight: 500,
                                fontSize: '0.8rem',
                                px: 2,
                                py: 1,
                                border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                                transition: 'all 0.2s cubic-bezier(0.4, 0.0, 0.2, 1)',
                                bgcolor: dialogSelectedCategory?.id === category.id ? 'black' : 'transparent',
                                color: dialogSelectedCategory?.id === category.id ? 'white' : 'inherit',
                                '&:hover': {
                                  bgcolor: dialogSelectedCategory?.id === category.id ? 'black' : 'transparent',
                                  border: `1px solid ${theme.palette.primary.main}`,
                                  transform: 'translateY(-1px)',
                                  boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.2)}`
                                }
                              }}
                            >
                              Select
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                      {getFilteredCategoriesForSelection().length === 0 && (
                        <TableRow>
                          <TableCell colSpan={2} sx={{ textAlign: 'center', py: 4 }}>
                            <Typography variant="body2" color="text.secondary">
                              {categorySearchTerm ? 'No categories found matching your search.' : 'No categories available.'}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </Box>
              </DialogContent>
              <DialogActions sx={{ 
                p: 3, 
                gap: 2, 
                borderTop: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`,
                bgcolor: alpha(theme.palette.grey[50], 0.5),
                display: 'flex',
                justifyContent: 'flex-end'
              }}>
                <Button
                  onClick={handleCloseCategorySelectionDialog}
                  variant="outlined"
                  sx={{ 
                    borderRadius: 2, 
                    px: 3, 
                    py: 1, 
                    fontSize: '0.9rem', 
                    fontWeight: 500,
                    textTransform: 'none',
                    border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                    '&:hover': {
                      border: `1px solid ${theme.palette.grey[600]}`,
                      transform: 'translateY(-1px)'
                    }
                  }}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleConfirmCategorySelection}
                  variant="contained"
                  disabled={!dialogSelectedCategory}
                  sx={{ 
                    borderRadius: 2, 
                    px: 3, 
                    py: 1, 
                    fontSize: '0.9rem', 
                    fontWeight: 500,
                    textTransform: 'none',
                    background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                    '&:hover': {
                      background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
                      transform: 'translateY(-1px)',
                      boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.3)}`
                    },
                    '&:disabled': {
                      background: alpha(theme.palette.grey[400], 0.5),
                      color: alpha(theme.palette.grey[600], 0.7)
                    }
                  }}
                >
                  Confirm Selection
                </Button>
              </DialogActions>
            </Dialog>

            {/* Snackbar */}
            <Snackbar
              open={snackbar.open}
              autoHideDuration={4000}
              onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
              <Alert severity={snackbar.severity} onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}>
                {snackbar.message}
              </Alert>
            </Snackbar>
          </Container>
        </Box>
      </Fade>
    </LocalizationProvider>
  );
};

export default ExpensesPage;